// Canvas/workspace header. Shares every icon and slot with the intranet
// navbar via the common `AppHeader` base — only canvas-specific bits
// diverge: floating position on the panzoom board, `CommandBar` in place
// of `SearchBar`, and an SDK-driven sign-out flow.

const CHROME_PATHS = new Set(['/dashboard', '/marketplace', '/account'])
const isChromeRoute = (s) => CHROME_PATHS.has(s.root?.currentPath)

// Canvas chrome routes that need a specific H1 title in the AppHeader.
// The shared AppHeader.pageTitle() falls back to 'Canvas' for unrecognised
// paths — /marketplace is canvas-only so it isn't in the shared TITLES map.
// List all canvas chrome paths here so PageTitle always renders a meaningful
// H1: heading hierarchy = H1 (AppHeader.PageTitle) → H2/H3 in page content.
const canvasPageTitle = (path) => {
  if (path === '/marketplace') return 'Marketplace'
  if (path === '/dashboard') return 'Dashboard'
  if (path === '/account') return 'Account'
  // Project canvas routes (/:owner/:key) — "Canvas" is correct.
  return 'Canvas'
}

export const CanvasAppHeader = {
  extends: 'AppHeader',
  position: (el, s) => (isChromeRoute(s) ? 'sticky' : 'fixed'),
  top: '0',
  left: (el, s) => (isChromeRoute(s) ? 'auto' : '0'),
  right: (el, s) => (isChromeRoute(s) ? 'auto' : '0'),
  zIndex: '100',
  paddingLeft: 'A',
  paddingRight: 'A',

  // Override PageTitle so canvas-specific chrome routes show a correct H1.
  // The base AppHeader renders PageTitle as `tag: 'span'` (semantic-neutral
  // for the intranet where a separate H1 lives elsewhere). On canvas chrome
  // routes (/marketplace, /dashboard, /account) this IS the page-level
  // heading, so we promote it to `tag: 'h1'` to satisfy heading hierarchy:
  //   H1 = AppHeader.PageTitle → H2/H3 = section titles in page content.
  //
  // Exception: when the 404 / not-found page is active (projectNotFound or
  // single-segment dead route), the notFound page itself owns the H1
  // (StatusCode). Demote PageTitle to a non-heading so there is exactly
  // one H1 on the page.
  PageTitle: {
    tag: (el, s) => {
      if (!s) return 'h1'
      if (s.root?.projectNotFound) return 'span'
      const path = typeof window !== 'undefined' ? window.location.pathname : (s.root?.currentPath || '')
      const segs = path.split('/').filter(Boolean)
      if (segs.length === 1) return 'span'
      return 'h1'
    },
    text: (el, s) => canvasPageTitle(s.root?.currentPath || s.root?.route || '')
  },

  SearchBar: null,

  CommandBar: {
    order: '1',
    flex: '1',
    maxWidth: 'H',
    margin: '- auto'
  },

  UserActions: {
    MeWrapper: {
      MeMenu: {
        SignOutItem: {
          href: '/signin',
          onClick: async (ev, el) => {
            const sdk = el.context?.methods?.sdk
            try { await sdk?.logout?.() } catch {}
            el.router('/signin', el.getRoot())
          }
        }
      }
    }
  }
}
